import { useEffect, useRef, useState } from 'react';
import ApexCharts from 'apexcharts';
import { api } from '@/api/growth/dashboard/api';
import {
    GrowthDashboardDaysReq,
    GrowingWeightListItem,
} from '@/api/growth/dashboard/type';
import Tooltip from '@components/common/Tooltip';

interface GrowthGrowingWeightInfoCardProps {
    gfIdx: number;
}

const GrowthGrowingWeightInfoCard = ({
    gfIdx,
}: GrowthGrowingWeightInfoCardProps) => {
    const chartRef = useRef<HTMLDivElement>(null);
    const chartInstance = useRef<ApexCharts | null>(null);
    const [loading, setLoading] = useState(true);
    const [growingWeightList, setGrowingWeightList] = useState<
        GrowingWeightListItem[]
    >([]);
    const [selectedDays, setSelectedDays] = useState<number>(14);

    // 생육무게 데이터 조회
    useEffect(() => {
        if (gfIdx) {
            fetchGrowingWeightInfo(gfIdx, selectedDays);
        }
    }, [gfIdx, selectedDays]);

    const fetchGrowingWeightInfo = async (gfIdx: number, days: number) => {
        try {
            setLoading(true);
            const req: GrowthDashboardDaysReq = { gfIdx, days };
            const response = await api.getGrowingWeightCardInfo(req);
            setGrowingWeightList(response.data || []);
        } catch (error) {
            console.error('생육무게 정보 조회 실패:', error);
            setGrowingWeightList([]);
        } finally {
            setLoading(false);
        }
    };

    // 차트 렌더링
    useEffect(() => {
        if (
            !chartRef.current ||
            !growingWeightList ||
            growingWeightList.length === 0
        ) {
            if (chartInstance.current) {
                chartInstance.current.destroy();
                chartInstance.current = null;
            }
            return;
        }

        // 기존 차트 인스턴스 제거
        if (chartInstance.current) {
            chartInstance.current.destroy();
        }

        // 데이터 추출
        const categories = growingWeightList.map(item => {
            const date = new Date(item.measurementDate);
            return `${date.getMonth() + 1}/${date.getDate()}`;
        });

        const rootWeightData = growingWeightList.map(
            item => item.rootWeight || 0,
        );
        const stemWeightData = growingWeightList.map(
            item => item.stemWeight || 0,
        );
        const leafWeightData = growingWeightList.map(
            item => item.leafWeight || 0,
        );
        const yieldWeightData = growingWeightList.map(
            item => item.yieldWeight || 0,
        );

        const options = {
            series: [
                {
                    name: '뿌리 무게',
                    data: rootWeightData,
                },
                {
                    name: '줄기 무게',
                    data: stemWeightData,
                },
                {
                    name: '잎 무게',
                    data: leafWeightData,
                },
                {
                    name: '수확물 무게',
                    data: yieldWeightData,
                },
            ],
            chart: {
                type: 'bar',
                height: 321,
                stacked: true,
                toolbar: {
                    show: false,
                },
                zoom: {
                    enabled: true,
                },
                dropShadow: {
                    enabled: true,
                    enabledOnSeries: undefined,
                    top: 3,
                    left: 0,
                    blur: 4,
                    color: '#000',
                    opacity: 0.1,
                },
            },
            grid: {
                borderColor: '#f1f1f1',
                strokeDashArray: 3,
            },
            yaxis: {
                title: {
                    text: '무게 (kg/ha)',
                },
                labels: {
                    formatter: function (val: number) {
                        return val.toFixed(1);
                    },
                },
            },
            responsive: [
                {
                    breakpoint: 480,
                    options: {
                        legend: {
                            position: 'bottom',
                            offsetX: -10,
                            offsetY: 0,
                        },
                    },
                },
            ],
            colors: [
                '#b59468', // 뿌리 무게
                '#69ac53', // 줄기 무게
                '#2ddf6b', // 잎 무게
                '#d3dc99', // 수확물 무게
            ],
            legend: {
                show: true,
                position: 'top',
                markers: {
                    size: 5,
                    shape: 'circle',
                },
            },
            stroke: {
                curve: 'smooth',
                width: [0, 0, 0, 0],
            },
            plotOptions: {
                bar: {
                    columnWidth: '60%',
                    borderRadius: 2,
                },
            },
            dataLabels: {
                enabled: false,
            },
            xaxis: {
                categories: categories,
                labels: {
                    rotate: -45,
                    rotateAlways: false,
                },
            },
            fill: {
                opacity: 1,
            },
            tooltip: {
                y: {
                    formatter: function (val: number) {
                        return val.toFixed(1) + ' kg/ha';
                    },
                },
            },
        };

        chartInstance.current = new ApexCharts(chartRef.current, options);
        chartInstance.current.render();

        return () => {
            if (chartInstance.current) {
                chartInstance.current.destroy();
            }
        };
    }, [growingWeightList]);

    // 기간 선택 버튼 핸들러
    const handleDaysChange = (days: number) => {
        setSelectedDays(days);
    };

    return (
        <div className="custom-card card">
            <div className="card-header justify-content-between align-items-center d-sm-flex d-block">
                <div className="card-title mb-sm-0 mb-2">
                    생육무게정보
                    <Tooltip
                        content="작물의 뿌리, 줄기, 잎, 수확물 무게의 변화 추이를 보여줍니다. 각 부위별 성장 패턴을 통해 작물의 전반적인 생육 상태를 파악할 수 있습니다."
                        customClass="tooltip-dark"
                        placement="top"
                    >
                        <i className="ri-question-line" />
                    </Tooltip>
                </div>
                <div
                    aria-label="Basic example"
                    role="group"
                    className="flex-wrap btn-group"
                >
                    <button
                        type="button"
                        className={`btn-wave btn ${selectedDays === 7 ? 'btn-success' : 'btn-success-light'}`}
                        onClick={() => handleDaysChange(7)}
                    >
                        7일
                    </button>
                    <button
                        type="button"
                        className={`btn-wave btn ${selectedDays === 14 ? 'btn-success' : 'btn-success-light'}`}
                        onClick={() => handleDaysChange(14)}
                    >
                        14일
                    </button>
                    <button
                        type="button"
                        className={`btn-wave btn ${selectedDays === 30 ? 'btn-success' : 'btn-success-light'}`}
                        onClick={() => handleDaysChange(30)}
                    >
                        30일
                    </button>
                    <button
                        type="button"
                        className={`btn-wave btn ${selectedDays === 60 ? 'btn-success' : 'btn-success-light'}`}
                        onClick={() => handleDaysChange(60)}
                    >
                        60일
                    </button>
                    <button
                        type="button"
                        className={`btn-wave btn ${selectedDays === -1 ? 'btn-success' : 'btn-success-light'}`}
                        onClick={() => handleDaysChange(-1)}
                    >
                        전체
                    </button>
                </div>
            </div>
            <div className="card-body">
                {loading ? (
                    <div
                        className="d-flex align-items-center justify-content-center"
                        style={{ height: '321px' }}
                    >
                        <div
                            className="spinner-border text-success"
                            role="status"
                        >
                            <span className="visually-hidden">로딩중...</span>
                        </div>
                    </div>
                ) : growingWeightList.length === 0 ? (
                    <div
                        className="d-flex align-items-center justify-content-center"
                        style={{ height: '321px' }}
                    >
                        <div className="text-center text-muted">
                            생육무게 데이터가 없습니다
                        </div>
                    </div>
                ) : (
                    <div ref={chartRef}></div>
                )}
            </div>
        </div>
    );
};

export default GrowthGrowingWeightInfoCard;
